import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, BadgeCheck, ChevronDown } from "lucide-react";
import { navMenu, company } from "../mock";
import { Button } from "./ui/button";
import Logo from "./Logo";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all ${
        scrolled ? "bg-white/95 backdrop-blur shadow-sm" : "bg-white"
      }`}
    >
      {/* Announcement bar */}
      <div className="bg-[#2E9E6A] text-white text-sm">
        <div className="mx-auto max-w-7xl px-4 py-2 flex items-center justify-center gap-2 text-center font-medium">
          <BadgeCheck className="h-4 w-4 shrink-0" />
          <span data-testid="announcement-text" className="font-semibold">
            NOW ACCEPTING NEW CLIENTS — MyCare Ohio & DODD
          </span>
          <a href={`tel:${company.phoneRaw}`} className="underline underline-offset-2 hover:opacity-90 shrink-0">
            {company.phone}
          </a>
        </div>
      </div>

      {/* Top bar */}
      <div className="brand-gradient text-white text-sm">
        <div className="mx-auto max-w-7xl px-4 py-2 flex items-center justify-between">
          <span className="hidden sm:block opacity-90">{company.address}, {company.city}</span>
          <a href={`tel:${company.phoneRaw}`} className="flex items-center gap-2 font-medium hover:opacity-90">
            <Phone className="h-4 w-4" /> {company.phone}
          </a>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4">
        <div className="flex h-20 items-center justify-between">
          <Logo />

          <nav className="hidden lg:flex items-center gap-7">
            {navMenu.map((l) => (
              <div key={l.label} className="relative group">
                <Link
                  to={l.path}
                  className={`nav-underline inline-flex items-center gap-1 text-[15px] font-medium text-slate-700 hover:text-[#0E5A72] ${
                    location.pathname === l.path ? "active text-[#0E5A72]" : ""
                  }`}
                >
                  {l.label}
                  {l.children && <ChevronDown className="h-3.5 w-3.5 opacity-60" />}
                </Link>
                {l.children && (
                  <div className="absolute left-0 top-full pt-3 hidden group-hover:block z-50">
                    <div className="bg-white rounded-xl shadow-xl border border-slate-100 py-2 min-w-[230px]">
                      {l.children.map((c) => (
                        <Link
                          key={c.path}
                          to={c.path}
                          className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-[#F7FBFC] hover:text-[#0E5A72]"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link to="/contact">
              <Button data-testid="header-request-btn" className="bg-[#2E9E6A] hover:bg-[#23875A] text-white rounded-full px-5 font-semibold">
                REQUEST HOME CARE
              </Button>
            </Link>
            <Link to="/refer">
              <Button data-testid="header-refer-btn" className="bg-[#0E5A72] hover:bg-[#0A4557] text-white rounded-full px-5 font-semibold">
                REFER A CLIENT
              </Button>
            </Link>
          </div>

          <button className="lg:hidden p-2 text-[#0E5A72]" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t bg-white px-4 pb-4 max-h-[70vh] overflow-y-auto">
          <nav className="flex flex-col py-2">
            {navMenu.map((l) => (
              <div key={l.label}>
                <Link to={l.path} className="block py-3 text-slate-700 font-semibold border-b border-slate-100">
                  {l.label}
                </Link>
                {l.children && l.children.map((c) => (
                  <Link key={c.path} to={c.path} className="block py-2.5 pl-5 text-sm text-slate-600 border-b border-slate-50">
                    {c.label}
                  </Link>
                ))}
              </div>
            ))}
            <Link to="/contact" className="mt-3">
              <Button className="w-full bg-[#2E9E6A] hover:bg-[#23875A] text-white rounded-full font-semibold">REQUEST HOME CARE</Button>
            </Link>
            <Link to="/refer" className="mt-2">
              <Button className="w-full bg-[#0E5A72] hover:bg-[#0A4557] text-white rounded-full font-semibold">REFER A CLIENT</Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
